import axios from "axios";

import { bus } from "@/bus";

const APP_IDENTITY_COOKIE = "appIdentity";
const APP_IDENTITY_TTL = "365d";

export function parseIdentity(raw) {
	if (!raw) return { type: null, identifier: null };
	const colonIndex = raw.indexOf(":");

	if (colonIndex === -1) return { type: null, identifier: null };
	return {
		type: raw.substring(0, colonIndex),
		identifier: raw.substring(colonIndex + 1)
	};
}

/**
 * Registry of connector controllers keyed by type.
 * Each connector type (e.g. "novologik") registers its controller here
 * by listening to the "connectors.ready" bus event.
 */
const controllers = {};

const state = {
	byType: {},
	namesByType: {},
	orgConnectors: [],
	orgConnectorsLoaded: false
};

const getters = {
	isConnectorConnected: (state) => (type) => state.byType[type]?.connected || false,
	connectorCredentials: (state) => (type) => state.byType[type]?.credentials || {},
	orgConnectors: (state) => state.orgConnectors,
	hasOrgConnectors: (state) => state.orgConnectorsLoaded && state.orgConnectors.length > 0,
	orgConnectorTypes: (state) => state.orgConnectors.map(c => c.type)
};

const mutations = {
	setConnector(state, { type, name, data = {} }) {
		state.byType = {
			...state.byType,
			[type]: {
				connected: false,
				...(state.byType[type] || {}),
				...data
			}
		};

		if (name) {
			state.namesByType = { ...state.namesByType, [type]: name };
		}
	},

	removeConnector(state, { type }) {
		const { [type]: _, ...rest } = state.byType;

		state.byType = rest;
		const { [type]: __, ...restNames } = state.namesByType;

		state.namesByType = restNames;
	},

	setConnected(state, { type, connected }) {
		if (!state.byType[type]) return;
		state.byType[type] = { ...state.byType[type], connected };
	},

	setCredentials(state, { type, credentials }) {
		if (!state.byType[type]) return;
		state.byType[type] = {
			...state.byType[type],
			credentials: { ...(state.byType[type].credentials || {}), ...credentials }
		};
	},

	setOrgConnectors(state, list) {
		state.orgConnectors = Array.isArray(list) ? list : [];
		state.orgConnectorsLoaded = true;
	},

	resetOrgConnectors(state) {
		state.orgConnectors = [];
		state.orgConnectorsLoaded = false;
	}
};

const actions = {
	/**
	 * Register a connector type and its controller.
	 * Called by each connector module (e.g. novologik) on setup.
	 */
	register({ commit }, { type, name, controller }) {
		if (controller) controllers[type] = controller;
		commit("setConnector", { type, name });
	},

	/**
	 * Start a registered connector for a given userGroupId.
	 */
	async start({ commit }, { type, userGroupId, credentials = {} }) {
		const controller = controllers[type];

		if (!controller) return;

		commit("setCredentials", { type, credentials });
		await controller.start(userGroupId);
	},

	/**
	 * Stop a registered connector.
	 */
	async stop({ commit }, { type }) {
		const controller = controllers[type];

		if (!controller) return;

		await controller.stop();
		commit("setConnected", { type, connected: false });
	},

	/**
	 * Stop all registered connectors.
	 */
	async stopAll({ dispatch, state }) {
		await Promise.all(
			Object.keys(state.byType).map((type) => dispatch("stop", { type }))
		);
	},

	async fetchOrgConnectors({ commit }) {
		try {
			const { data } = await axios.get("/connectors");

			commit("setOrgConnectors", data || []);
		} catch (err) {
			console.error("[CONNECTORS] Failed to fetch org connectors:", err);
			commit("setOrgConnectors", []);
		}
	},

	/**
	 * Navigate to patient using a specific connector type.
	 * Desktop identity cookie is read here (only place it's used at runtime).
	 */
	async gotoPatientInConnector({ getters }, { type, patient }) {
		const controller = controllers[type];

		if (!controller?.isConnected()) return false;
		if (!getters.isConnectorConnected(type)) return false;

		const raw = $cookies.get(APP_IDENTITY_COOKIE) || "";
		const { identifier } = parseIdentity(raw);

		try {
			const success = await controller.gotoPatient(identifier, patient.fullname, patient.phone);

			// Renew cookie TTL only on success (preserve full type:identifier format)
			if (success && raw) {
				$cookies.set(APP_IDENTITY_COOKIE, raw, APP_IDENTITY_TTL);
			}

			return success;
		} catch (error) {
			console.error("[CONNECTORS] gotoPatient failed", { type, error });
			return false;
		}
	}
};

/**
 * Store init: watches userGroupId changes and emits bus events
 * so connector modules can self-start/stop.
 */
export function init({ watch, login, dispatch }, rootStore) {
	watch(
		() => rootStore.getters["login/loggedIn"],
		(newVal, oldVal) => {
			if (newVal === oldVal) return;

			console.log("[CONNECTORS] loggedIn changed:", oldVal, "->", newVal);
			
			if (newVal) {
				bus.$emit("connectors.ready");
				dispatch("fetchOrgConnectors");
			} else {
				bus.$emit("connectors.unready");
				dispatch("stopAll");
				rootStore.commit("connector/resetOrgConnectors");
			}
		}
	);

	watch(
		() => login.refreshed,
		(refreshed) => {
			if (refreshed) {
				bus.$emit("connectors.ready");
				dispatch("fetchOrgConnectors");
			}
		}
	);
}

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
	init
};
